import { useEffect, useReducer, useRef, useState } from "react";
import { useManualOccurrence } from "contexts/register/ManualOccurrence";
import OccurrenceManagementService from "services/OccurrenceManagementService";
import * as S from "./styles";

import { ReactComponent as RightArrowIcon } from "assets/right-small.svg";

import { MapContainer, TileLayer, Marker } from "react-leaflet";

import Swal from "sweetalert2";

import L from "leaflet";
import "leaflet-arrowheads";
import { foundMarker, lastLocationMarker } from "./LastLocationsMarker";
import { fireToast } from "services/sweetAlertConfig";
import { INITIAL_COORDS_STATE, coordinatesReducer } from "./reducers";
import { AddButton } from "../VehicleDataTab/styles";
import { Button } from "components/Button";

import { InputMap } from "components/InputMap";

const initialCenterPosition = {
  lat: -15.7303544,
  lng: -47.9325513,
};

type Coords = {
  lat: number;
  lng: number;
};

const swal = Swal.mixin({});

export default function LastLocationsTab() {
  const { occurrenceToEdit, refetch } = useManualOccurrence();
  const [isRequesting, setIsRequesting] = useState<boolean>(false);
  const [coordsData, dispatchCoordsData] = useReducer(
    coordinatesReducer,
    INITIAL_COORDS_STATE
  );

  const mapRef = useRef<L.Map | null>(null);

  const markers = occurrenceToEdit?.lastCoords.map((coords, index) => ({
    id: index,
    coords: {
      lat: coords[0],
      lng: coords[1],
    },
  }));

  // function to remove a coordinate from the coordinates array of the occurrence
  async function handleRemoveCoordinate(coordinate: [number, number]) {
    setIsRequesting(true);

    swal
      .fire({
        title: "Alerta",
        text: `Você está prestes a REMOVER uma das possíveis localizações registradas do veículo (${coordinate[0]}, ${coordinate[1]}). Deseja continuar?`,
        icon: "warning",
        showConfirmButton: true,
        showDenyButton: true,
        confirmButtonText: "Continuar",
        denyButtonText: "Cancelar",
        customClass: {
          confirmButton: "swal-confirm-btn",
          denyButton: "swal-deny-btn",
        },
      })
      .then(async (res) => {
        if (res.isConfirmed) {
          const newCoordinates = occurrenceToEdit.lastCoords.filter(
            ([x, y]) => !(coordinate[0] === x && coordinate[1] === y)
          );

          await OccurrenceManagementService.removeLastCoord(
            occurrenceToEdit.id,
            newCoordinates
          );

          refetch();
        }
        setIsRequesting(false);
      });
  }

  function handleMarkerClick(coords: Coords) {
    mapRef.current?.setView({ lat: coords.lat, lng: coords.lng }, 14);
  }

  function handleMapRefLoad(ref: L.Map | null) {
    mapRef.current = ref;

    if (ref) {
      const polyline = L.polyline(occurrenceToEdit.lastCoords, {
        color: "#2E2D2D",
      }).arrowheads({
        size: "3%",
      });

      mapRef.current?.addLayer(polyline);

      if (occurrenceToEdit.lastCoords.length > 0) {
        mapRef.current?.fitBounds(occurrenceToEdit.lastCoords);
      }
    }
  }

  async function handleFoundLocation(coords: [number, number]) {
    swal
      .fire({
        title: "Aviso",
        text: `Você está prestes a sinalizar que o veículo foi encontrado nessa coordenada (${coords[0]}, ${coords[1]}). Deseja continuar?`,
        icon: "warning",
        showConfirmButton: true,
        showDenyButton: true,
        confirmButtonText: "Continuar",
        denyButtonText: "Cancelar",
        customClass: {
          confirmButton: "swal-confirm-btn",
          denyButton: "swal-deny-btn",
        },
      })
      .then(async (res) => {
        if (res.isConfirmed) {
          setIsRequesting(true);

          await OccurrenceManagementService.updateFoundCoords(
            occurrenceToEdit.id,
            coords
          ).finally(() => {
            setIsRequesting(false);
          });

          fireToast("Localização atual do veículo atualizada com sucesso!");

          refetch();
        }
      });
  }

  function isCoordsActive(coords: [number, number]) {
    return occurrenceToEdit.findCoords !== undefined &&
      occurrenceToEdit?.findCoords[0] === coords[0] &&
      occurrenceToEdit?.findCoords[1] === coords[1]
      ? "var(--primary)"
      : undefined;
  }

  // function to dispatch the new coordinate array to the array of coords
  async function addCoords() {
    if (coordsData.lastCoordX !== 0) {
      dispatchCoordsData({
        type: "APPEND_TO_LAST_COORDS",
        payload: [coordsData.lastCoordX, coordsData.lastCoordY],
      });

      fireToast(
        "Localização adicionada com sucesso (não se esqueça de salvar antes de fechar)"
      );
    }
  }

  // function to update the occurrence coordinates array
  async function handleUpdateCoords() {
    setIsRequesting(true);

    await OccurrenceManagementService.updateLastCoords(
      occurrenceToEdit.id,
      coordsData.lastCoords,
      coordsData.formattedAddress
    );

    setIsRequesting(false);

    refetch();
  }

  // useEffect to set the last coords reducer as the occurrence last coords if it exists
  useEffect(() => {
    dispatchCoordsData({
      type: "SET_LAST_COORDS",
      payload: occurrenceToEdit.lastCoords ?? [],
    });
  }, [occurrenceToEdit]);

  return (
    <S.Wrapper>
      <S.ContentColumn>
        <S.Body>
          {occurrenceToEdit?.findCoords &&
            occurrenceToEdit?.findCoords.length > 0 && (
              <S.FoundCoordContainer>
                <h3>O veículo foi encontrado em</h3>

                <S.FoundCard
                  onClick={() => {
                    handleMarkerClick({
                      lat: occurrenceToEdit?.findCoords?.[0] ?? 0,
                      lng: occurrenceToEdit?.findCoords?.[1] ?? 0,
                    });
                  }}
                >
                  <span>
                    {occurrenceToEdit?.findCoords[0]},{" "}
                    {occurrenceToEdit?.findCoords[1]}
                  </span>
                  <button>
                    <RightArrowIcon />
                  </button>
                </S.FoundCard>
              </S.FoundCoordContainer>
            )}

          {occurrenceToEdit.status !== "concluido" && (
            <div>
              <InputMap
                label="Localização atual do veículo - (Opcional)"
                onCoordsChange={(coords) => {
                  dispatchCoordsData({
                    type: "SET_COORD_X",
                    payload: coords.lat,
                  });
                  dispatchCoordsData({
                    type: "SET_COORD_Y",
                    payload: coords.lng,
                  });
                }}
                onFormattedAddressChange={(formattedAddress) => {
                  dispatchCoordsData({
                    type: "SET_FORMATTED_ADDRESS",
                    payload: formattedAddress,
                  });
                }}
                location={{
                  lat: coordsData.lastCoordX,
                  lng: coordsData.lastCoordY,
                }}
              />

              <AddButton
                type="button"
                onClick={() => {
                  addCoords();
                }}
              >
                Adicionar como uma nova localização +
              </AddButton>
            </div>
          )}

          <h3>Coordenadas registradas</h3>

          {coordsData.lastCoords.length === 0 && (
            <h4>Ainda não há coordenadas registradas para essa ocorrência.</h4>
          )}

          {coordsData.lastCoords.length > 0 && (
            <ul>
              {coordsData.lastCoords?.map((Coord, index) => {
                if (Coord[0] === null && Coord[1] === null) return <></>;

                return (
                  <li key={index} data-cy="last-locations-tab-registered-coord">
                    <S.ListCoordItemHeader>
                      {Coord[0]}, {Coord[1]}
                    </S.ListCoordItemHeader>

                    <S.ListCoordItemBody>
                      {occurrenceToEdit.status !== "concluido" && (
                        <button
                          onClick={() =>
                            handleFoundLocation([Coord[0], Coord[1]])
                          }
                          className="highlighted"
                          disabled={isRequesting}
                        >
                          Encontrado aqui
                        </button>
                      )}

                      <button
                        onClick={() =>
                          handleMarkerClick({
                            lat: Coord[0],
                            lng: Coord[1],
                          })
                        }
                      >
                        Ver no mapa
                      </button>

                      {occurrenceToEdit.status !== "concluido" && (
                        <button
                          onClick={() =>
                            handleRemoveCoordinate([Coord[0], Coord[1]])
                          }
                        >
                          Remover
                        </button>
                      )}
                    </S.ListCoordItemBody>
                  </li>
                );
              })}
            </ul>
          )}
        </S.Body>
        {occurrenceToEdit.status !== "concluido" && (
          <S.Footer>
            <Button
              title="Salvar novas coordenadas"
              isFetching={isRequesting}
              onClick={handleUpdateCoords}
              variant="primary"
            />
          </S.Footer>
        )}
      </S.ContentColumn>
      <S.MapColumn>
        <MapContainer
          className="map-container"
          center={initialCenterPosition}
          scrollWheelZoom
          zoom={4}
          ref={handleMapRefLoad}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />

          {markers.map((marker) => (
            <Marker
              icon={
                isCoordsActive([marker.coords.lat, marker.coords.lng])
                  ? foundMarker()
                  : lastLocationMarker()
              }
              position={{
                lat: marker.coords.lat,
                lng: marker.coords.lng,
              }}
            />
          ))}

          {occurrenceToEdit?.findCoords &&
            occurrenceToEdit?.findCoords.length > 0 && (
              <Marker
                icon={foundMarker()}
                position={{
                  lat: occurrenceToEdit?.findCoords[0],
                  lng: occurrenceToEdit?.findCoords[1],
                }}
              />
            )}

          <S.CaptionWrapper>
            <S.CaptionContainer>
              <S.CaptionBody>
                <S.CaptionIcon variant="inactive" />
                <span>Última localização</span>
              </S.CaptionBody>
              <S.CaptionBody>
                <S.CaptionIcon variant="active" />
                <span>Localização do veículo encontrado</span>
              </S.CaptionBody>
            </S.CaptionContainer>
          </S.CaptionWrapper>
        </MapContainer>
      </S.MapColumn>
    </S.Wrapper>
  );
}
